import { useCallback, useRef, useState } from 'react';
import { toast } from '../../Components/Toast';
import {
  linkAdUnit,
  linkCampaignUnit,
  unlinkAdUnit,
  unlinkCampaignUnit,
} from '../../services/metaAdsService';
import { getMetaErrorMessage, needsMetaReconnect, readMetaErrorCode } from '../metaAdsConfig';
import {
  applyAdLinkResponse,
  applyCampaignLinkResponse,
  metaRecordKey,
  optimisticLinkAd,
  optimisticLinkCampaign,
  optimisticUnlinkAd,
  optimisticUnlinkCampaign,
  readMetaRecordErrorIndex,
} from '../metaCampaigns';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * Índice del registro con problema: `VALIDATION_ERROR` con `field: "records[i]"`
 * o `RECORD_NOT_FOUND` con `record` (se busca en lo mandado).
 */
export function findMetaLinkErrorIndex(res, records) {
  const fromField = readMetaRecordErrorIndex(res);
  if (fromField != null) return fromField;
  if (readMetaErrorCode(res) === 'RECORD_NOT_FOUND' && res?.record) {
    // 404 {code, field:"records", record:{item_kind,id}}: solo el primero que falta, id en minúsculas
    // (todo o nada: no se escribió ninguno). Se compara sin distinguir mayúsculas.
    const key = metaRecordKey({ itemKind: res.record.item_kind ?? res.record.itemKind, id: res.record.id });
    const i = (records || []).findIndex((r) => metaRecordKey(r) === key);
    return i >= 0 ? i : null;
  }
  return null;
}

const REFRESH_CODES = ['META_AD_NOT_FOUND', 'META_CAMPAIGN_NOT_FOUND'];

/**
 * Vincular / desvincular anuncios y campañas (dueño o editor con
 * `viewer_can_link`; el editor manda `owner_user_id` en el body vía
 * `ownerUserId`). Optimista con rollback: aplica el cambio en la lista, llama
 * a la API y, si falla, vuelve a la foto anterior. OK → aplica lo que devuelve
 * la API y llama `onChanged` (refrescar campañas y conexión).
 *
 * Cada acción devuelve el envelope. `silentError` (diálogo) no muestra toast:
 * el diálogo muestra el error al lado del registro.
 *
 * @param {{ ownerUserId?: string|null, campaignsState: object, onChanged?: () => void }} options
 */
export function useMetaLinkActions({ ownerUserId = null, campaignsState, onChanged }) {
  const [pending, setPending] = useState(() => new Set());
  const [reconnectError, setReconnectError] = useState(null);
  const pendingRef = useRef(new Set());
  const { setCampaigns, reload } = campaignsState;
  const campaignsRef = useRef(campaignsState.campaigns);
  campaignsRef.current = campaignsState.campaigns;

  const run = useCallback(async (key, { campaignId, optimistic, call, apply, successMessage, silentError = false }) => {
    if (pendingRef.current.has(key)) return null;
    pendingRef.current.add(key);
    setPending(new Set(pendingRef.current));
    // Foto de la campaña afectada (rollback solo de esa: no pisa otras acciones en curso).
    const snapshot = (campaignsRef.current || []).find((c) => c.id === campaignId) || null;
    setCampaigns((current) => optimistic(current));
    const res = await call();
    pendingRef.current.delete(key);
    setPending(new Set(pendingRef.current));
    if (res?._httpOk) {
      setReconnectError(null);
      setCampaigns((current) => apply(current, res.data));
      if (successMessage) toast.success({ message: successMessage(res) });
      onChanged?.();
      return res;
    }
    if (snapshot) setCampaigns((current) => current.map((c) => (c.id === snapshot.id ? snapshot : c)));
    if (needsMetaReconnect(res)) setReconnectError(getMetaErrorMessage(res));
    if (!silentError) toast.error({ title: 'No pudimos guardar el vínculo', message: getMetaErrorMessage(res) });
    if (REFRESH_CODES.includes(readMetaErrorCode(res))) reload({ silent: true });
    return res;
  }, [setCampaigns, reload, onChanged]);

  const linkAd = useCallback((ad, records, opts = {}) => run(`ad:${ad.id}`, {
    ...opts,
    campaignId: ad.campaignId,
    optimistic: (c) => optimisticLinkAd(c, ad.id, records),
    call: () => linkAdUnit({ adId: ad.id, records, ownerUserId }),
    apply: applyAdLinkResponse,
    successMessage: () => `Vinculaste «${ad.name}» con ${plural(records.length, 'unidad', 'unidades')}.`,
  }), [run, ownerUserId]);

  /** `records` null = desvincular de todas (solo tras confirmar). */
  const unlinkAd = useCallback((ad, records, opts = {}) => run(`ad:${ad.id}`, {
    ...opts,
    campaignId: ad.campaignId,
    optimistic: (c) => optimisticUnlinkAd(c, ad.id, records),
    call: () => unlinkAdUnit(records ? { adId: ad.id, records, ownerUserId } : { adId: ad.id, all: true, ownerUserId }),
    apply: applyAdLinkResponse,
    successMessage: () => (records ? 'Desvinculaste la unidad del anuncio.' : `Desvinculaste «${ad.name}» de todas sus unidades.`),
  }), [run, ownerUserId]);

  const linkCampaign = useCallback((campaign, records, opts = {}) => run(`campaign:${campaign.id}`, {
    ...opts,
    campaignId: campaign.id,
    optimistic: (c) => optimisticLinkCampaign(c, campaign.id, records),
    call: () => linkCampaignUnit({ campaignId: campaign.id, records, ownerUserId }),
    apply: applyCampaignLinkResponse,
    successMessage: (res) => {
      const ads = Number(res?.ads_count) || campaign.ads.length;
      return `Vinculaste ${plural(ads, 'anuncio', 'anuncios')} de «${campaign.name}».`;
    },
  }), [run, ownerUserId]);

  /** `records` null = desvincular toda la campaña (solo tras confirmar). */
  const unlinkCampaign = useCallback((campaign, records, opts = {}) => run(`campaign:${campaign.id}`, {
    ...opts,
    campaignId: campaign.id,
    optimistic: (c) => optimisticUnlinkCampaign(c, campaign.id, records),
    call: () => unlinkCampaignUnit(records
      ? { campaignId: campaign.id, records, ownerUserId }
      : { campaignId: campaign.id, all: true, ownerUserId }),
    apply: applyCampaignLinkResponse,
    successMessage: () => (records ? 'Quitaste la unidad de toda la campaña.' : `Desvinculaste toda la campaña «${campaign.name}».`),
  }), [run, ownerUserId]);

  const isPending = useCallback((key) => pending.has(key), [pending]);

  return { linkAd, unlinkAd, linkCampaign, unlinkCampaign, isPending, reconnectError };
}
